import { useState } from 'react'
import { SignedIn, SignedOut, SignIn, UserButton } from '@clerk/clerk-react'
import { NavLink, Route, Routes, Navigate } from 'react-router-dom'
import Projects from './pages/Projects.js'
import ProjectDetail from './pages/ProjectDetail.js'
import Members from './pages/Members.js'
import AccessTokens from './pages/AccessTokens.js'
import Authorize from './pages/Authorize.js'
import DeviceApprove from './pages/DeviceApprove.js'
import ConnectAgent from './pages/ConnectAgent.js'
import Database from './pages/Database.js'
import Deployments from './pages/Deployments.js'
import Domains from './pages/Domains.js'
import Auth from './pages/Auth.js'
import ProjectLayout from './components/ProjectLayout.js'
import { PermissionsProvider } from './components/Permissions.js'
import ProjectKeys from './pages/ProjectKeys.js'
import ProjectSettings from './pages/ProjectSettings.js'
import Diagnostics from './pages/Diagnostics.js'
import Logs from './pages/Logs.js'
import Storage from './pages/Storage.js'
import Vectors from './pages/Vectors.js'
import Realtime from './pages/Realtime.js'
import { OrgProvider, useOrgs } from './lib/orgs.js'
import { Dialog, ErrorBox, until } from './components/ui.js'

/**
 * The no-organizations screen.
 *
 * Rare by design: every account gets a personal workspace at sign-up. This
 * is reached when that creation failed, or after leaving every organization
 * - so it offers both paths out, creating one or accepting an invitation.
 */
function Onboarding() {
  const { createOrganization, acceptInvitation, invitations } = useOrgs()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createOrganization(name.trim())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the organization.')
      setBusy(false)
    }
  }

  return (
    <main className="main" id="main-content" tabIndex={-1}>
      <div className="page-head">
        <div>
          <h1>Welcome to NimblyBase</h1>
          <p className="sub">
            Everything here - projects, databases, API keys - lives inside an organization. Create one to get
            started.
          </p>
        </div>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      <form className="card" onSubmit={create} style={{ padding: 16, marginBottom: 16 }}>
        <div className="form-row">
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="org-name">Organization name</label>
            <input
              id="org-name"
              value={name}
              required
              maxLength={100}
              placeholder="My Company"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create organization'}
          </button>
        </div>
        <p className="sub small" style={{ marginTop: 10 }}>
          You become its owner. You can invite your team from the Members page.
        </p>
      </form>

      {invitations.length > 0 && (
        <>
          <h2>Invitations waiting for you</h2>
          <div className="card">
            {invitations.map((inv) => (
              <div className="row" key={inv.id}>
                <div className="row-main">
                  <div className="row-title">{inv.orgName}</div>
                  <div className="row-sub">
                    as {inv.role} · expires {until(inv.expiresAt)}
                  </div>
                </div>
                <button className="btn btn-primary" onClick={() => acceptInvitation(inv.id)}>
                  Accept
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </main>
  )
}

/** Create an organization from anywhere in the app. */
function CreateOrgDialog({ onClose }: { onClose: () => void }) {
  const { createOrganization } = useOrgs()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createOrganization(name.trim())
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the organization.')
      setBusy(false)
    }
  }

  return (
    <Dialog open title="New organization" onClose={onClose} busy={busy}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}>
        <p className="sub" style={{ marginBottom: 14 }}>
          A separate space with its own projects, members and billing.
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="field">
          <label htmlFor="new-org-name">Name</label>
          <input
            id="new-org-name"
            value={name}
            required
            maxLength={100}
            autoFocus
            placeholder="Acme Inc"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * The switcher. Choosing the last entry opens the create dialog rather than
 * switching - the only place in the app that creates an organization once
 * you already have one, so it cannot be a hidden affordance.
 */
const NEW_ORG = '__new__'

function OrgSwitcher() {
  const { organizations, active, switchTo } = useOrgs()
  const [creating, setCreating] = useState(false)
  if (!active) return null

  return (
    <>
      <select
        className="org-switcher"
        value={active.id}
        onChange={(e) => {
          if (e.target.value === NEW_ORG) setCreating(true)
          else switchTo(e.target.value)
        }}
        aria-label="Active organization"
      >
        {organizations.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.isPersonal ? ' (personal)' : ''}
          </option>
        ))}
        <option disabled>──────────</option>
        <option value={NEW_ORG}>+ New organization…</option>
      </select>
      {creating && <CreateOrgDialog onClose={() => setCreating(false)} />}
    </>
  )
}

/** The API could not be reached. Not onboarding - the user may well have orgs. */
function Unreachable({ message }: { message: string }) {
  const { refresh } = useOrgs()
  return (
    <main className="main" id="main-content" tabIndex={-1}>
      <div className="card" style={{ padding: 20 }}>
        <h2 style={{ marginTop: 0 }}>Could not reach NimblyBase</h2>
        <ErrorBox>{message}</ErrorBox>
        <p className="sub" style={{ margin: '10px 0 14px' }}>
          Your organizations and projects are safe - this page just could not load them. If this is the UAT
          environment outside working hours, it may be asleep.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => {
            void refresh()
          }}
        >
          Try again
        </button>
      </div>
    </main>
  )
}

function Shell() {
  const { loading, loadError, active } = useOrgs()

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="topbar">
        <span className="brand">
          Nimbly<em>Base</em>
        </span>
        <span className="environment-badge">
          {new URL(import.meta.env.VITE_API_URL ?? 'https://api-uat.nimblybase.com').hostname ===
          'api-uat.nimblybase.com'
            ? 'UAT'
            : 'Workspace'}
        </span>
        <nav className="nav">
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/connect">Connect agent</NavLink>
          <NavLink to="/members">Members</NavLink>
          <NavLink to="/tokens">Access tokens</NavLink>
        </nav>
        <div className="spacer" />
        <OrgSwitcher />
        <UserButton />
      </header>

      {loading ? (
        <main className="centered muted" id="main-content" tabIndex={-1}>Loading…</main>
      ) : loadError ? (
        <Unreachable message={loadError} />
      ) : !active ? (
        <Onboarding />
      ) : (
        <main className="main" id="main-content" tabIndex={-1} key={active.id}>
          <PermissionsProvider>
            <Routes>
              <Route path="/" element={<Navigate to="/projects" replace />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectLayout />}>
                <Route index element={<ProjectDetail />} />
                <Route path="database" element={<Database />} />
                <Route path="deployments" element={<Deployments />} />
                <Route path="domains" element={<Domains />} />
                <Route path="auth" element={<Auth />} />
                <Route path="keys" element={<ProjectKeys />} />
                <Route path="settings" element={<ProjectSettings />} />
                <Route path="storage" element={<Storage />} />
                <Route path="vectors" element={<Vectors />} />
                <Route path="realtime" element={<Realtime />} />
                <Route path="logs" element={<Logs />} />
                <Route path="diagnostics" element={<Diagnostics />} />
              </Route>
              <Route path="/oauth/authorize" element={<Authorize />} />
              <Route path="/oauth/device" element={<DeviceApprove />} />
              <Route path="/connect" element={<ConnectAgent />} />
              <Route path="/members" element={<Members />} />
              <Route path="/tokens" element={<AccessTokens />} />
              <Route path="*" element={<div className="empty">Page not found.</div>} />
            </Routes>
          </PermissionsProvider>
        </main>
      )}
    </div>
  )
}

export default function App() {
  return (
    <>
      <SignedIn>
        <OrgProvider>
          <Shell />
        </OrgProvider>
      </SignedIn>
      <SignedOut>
        <div className="centered">
          <SignIn routing="hash" />
        </div>
      </SignedOut>
    </>
  )
}
